"use client";

import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { FIRST_PARTY_COOKIES } from "shared/SharedConstants";
import { hydrateAllDeferred } from "src/components/common/DeferredHydration/DeferredHydrationGates";
import { getCookie } from "src/lib/AppSharedUtils";
import { type DocsLanguage, parseDocsLanguage } from "src/lib/DocsLanguage";
import { type DocsUiLibrary, parseDocsUiLibrary } from "src/lib/DocsUiLibrary";
import { setCookie } from "src/lib/hooks/ClientHooks";
import { type DocsChoiceStore, type DocsChoices, DocsLanguageContext } from "./DocsLanguageContext";

type ScrollAnchor = { current: { element: HTMLElement; top: number } | null };

const createChoiceStore = <T extends string>(
  initial: T,
  cookie: { name: string; expirationMs: number },
  scrollAnchor: ScrollAnchor,
): DocsChoiceStore<T> => {
  let current = initial;
  const listeners = new Set<() => void>();
  const store: DocsChoiceStore<T> = {
    initial,
    get: () => current,
    set: (value) => {
      if (value === current) return;
      current = value;
      // Endpoint cards that haven't hydrated yet show the previous choice: hydrate them (in the
      // background) so they switch too.
      hydrateAllDeferred("visible");
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    select: (value, anchor) => {
      if (value === current) return;
      // Keep the example being read in place when other snippets change height above it.
      scrollAnchor.current = anchor
        ? { element: anchor, top: anchor.getBoundingClientRect().top }
        : null;
      store.set(value);
      setCookie(cookie.name, value, { expiresMs: cookie.expirationMs });
    },
  };
  return store;
};

export default function DocsLanguageProvider({
  initialLanguage,
  initialUiLibrary = "react",
  children,
}: {
  initialLanguage: DocsLanguage;
  initialUiLibrary?: DocsUiLibrary;
  children: React.ReactNode;
}) {
  const scrollAnchor = useRef<{ element: HTMLElement; top: number } | null>(null);
  const [choices] = useState<DocsChoices>(() => ({
    language: createChoiceStore(initialLanguage, FIRST_PARTY_COOKIES.DOCS_LANGUAGE, scrollAnchor),
    uiLibrary: createChoiceStore(
      initialUiLibrary,
      FIRST_PARTY_COOKIES.DOCS_UI_LIBRARY,
      scrollAnchor,
    ),
  }));
  const language = useSyncExternalStore(
    choices.language.subscribe,
    choices.language.get,
    () => choices.language.initial,
  );
  const uiLibrary = useSyncExternalStore(
    choices.uiLibrary.subscribe,
    choices.uiLibrary.get,
    () => choices.uiLibrary.initial,
  );

  useLayoutEffect(() => {
    const anchor = scrollAnchor.current;
    scrollAnchor.current = null;
    if (!anchor?.element.isConnected) return;
    const offset = anchor.element.getBoundingClientRect().top - anchor.top;
    if (offset !== 0) {
      window.scrollTo({ top: window.scrollY + offset, left: window.scrollX, behavior: "instant" });
    }
  }, [language, uiLibrary]);
  useEffect(() => {
    // Recover in-memory preferences too, without reading browser state during hydration.
    const storedLanguage = getCookie(FIRST_PARTY_COOKIES.DOCS_LANGUAGE.name);
    if (storedLanguage) choices.language.set(parseDocsLanguage(storedLanguage));
    const storedUiLibrary = getCookie(FIRST_PARTY_COOKIES.DOCS_UI_LIBRARY.name);
    if (storedUiLibrary) choices.uiLibrary.set(parseDocsUiLibrary(storedUiLibrary));
  }, [choices]);

  return <DocsLanguageContext value={choices}>{children}</DocsLanguageContext>;
}
